import type { CourseTeeSet, CourseTeeSetData, TbRegistration } from '../../../shared/config/iTournamentBuilder';
import {
    parseStartingHoleNumber,
    resolveRegistrationStartingHole,
} from './scoreCardTypes';

// ─── Canvas constants ─────────────────────────────────────────────────────────
// 11" × 4.25" at 72 pt/in — resolution-independent; all font sizes scale relative
// to SCORECARD_CANVAS_W so the card looks identical at any display width.
export const SCORECARD_CANVAS_W = 792;
export const SCORECARD_CANVAS_H = 306;

// 1 inch expressed as % of canvas height — used to constrain marker tear-away grids.
export const ONE_INCH_H_PCT = (1 / 4.25) * 100; // ≈ 23.53 %

// ─── Element types ────────────────────────────────────────────────────────────

export type ScorecardElementType = 'text' | 'image' | 'qrcode' | 'line' | 'scorecardGrid';

export type TextAlign = 'left' | 'center' | 'right';

// ─── Scorecard grid configuration ─────────────────────────────────────────────

export interface ScorecardGridConfig {
    /** Show the par row */
    showPar: boolean;
    /** Show the stroke-index (handicap) row */
    showHandicap: boolean;
    /** Show the yardage / distance row */
    showDistance: boolean;
    /** Show player score rows */
    showPlayer: boolean;
    /**
     * How many player score rows to render.
     * 'auto' = one row per player in the scoring unit at render time.
     * A number forces a fixed row count regardless of unit size.
     */
    playerRows: 'auto' | number;
    /** Show the marker score row */
    showMarker: boolean;
    /** Show net-strokes dots on score cells */
    showStrokes: boolean;
    /** Insert a fold-gap column between hole 9 and hole 10 */
    showFoldGap: boolean;
    /** Width of the fold-gap column in pixels (designer scale) */
    foldGapPx: number;
    /**
     * Explicit height for player (and marker) score rows in pixels at designer
     * scale. When undefined the row height is determined by font size + padding.
     */
    playerRowHeightPx?: number;
    /** Semantic role — controls labelling and position constraints */
    gridRole: 'player' | 'marker';
    /**
     * Tear-away position for marker grids.
     * 'top'    → grid is constrained within the top 1" of the card.
     * 'bottom' → grid is constrained within the bottom 1" of the card.
     * Undefined means no positional constraint.
     */
    markerPosition?: 'top' | 'bottom';
}

// ─── Element definition ───────────────────────────────────────────────────────

export interface ScorecardElement {
    id: string;
    type: ScorecardElementType;
    /** Position as percentage of canvas width/height (0–100) */
    x: number;
    y: number;
    /** Size as percentage of canvas width/height (0–100) */
    width: number;
    height: number;

    // ── text ──────────────────────────────────────────────────────────────────
    /** Static text or binding tokens such as "{{eventName}}" */
    content?: string;
    fontSize?: number;
    fontWeight?: 'normal' | 'bold';
    color?: string;
    textAlign?: TextAlign;

    // ── image ─────────────────────────────────────────────────────────────────
    /** Base-64 data URL or CDN URL for image elements */
    imageDataUrl?: string;

    // ── line ──────────────────────────────────────────────────────────────────
    lineOrientation?: 'horizontal' | 'vertical';
    lineColor?: string;
    /** Thickness in pixels (designer scale); default 1 */
    lineThickness?: number;

    // ── scorecardGrid ─────────────────────────────────────────────────────────
    gridConfig?: ScorecardGridConfig;

    /** When false, hidden from preview/print but kept in template. Default true. */
    visible?: boolean;
}

// ─── Template ─────────────────────────────────────────────────────────────────

export interface ScorecardTemplate {
    backgroundColor: string;
    elements: ScorecardElement[];
}

// ─── Scoring unit ─────────────────────────────────────────────────────────────
// One card is printed per scoring unit (= one score code, which may represent
// 1 individual or multiple players in a team format).

export interface ScoringUnit {
    scoreCode: string;
    eventScoreID: string | undefined;
    /** Combined display name — individual names joined with " / " */
    playerName: string;
    /** Individual player names in registration order */
    playerNames: string[];
    /** Joined initials for grid row label, e.g. "JD / JS" */
    initialsLabel: string;
    /** Tee time string from the first registration for the current round */
    teeTime: string | null;
    /** Tee-set name from the first registration */
    teeSet: string | undefined;
    /** Number of registrations that share this score code */
    registrationCount: number;
    /** Source registrations (in join order) */
    registrations: TbRegistration[];
}

// ─── Data binding tokens ──────────────────────────────────────────────────────

export const SCORECARD_BINDING_TOKENS = [
    // ── registration-level ────────────────────────────────────────────────────
    { token: '{{playerName}}',  label: 'Player Name' },
    { token: '{{teamName}}',    label: 'Team Name' },
    { token: '{{scoreCode}}',   label: 'Score Code' },
    { token: '{{teeTime}}',     label: 'Tee Time' },
    { token: '{{startingHole}}', label: 'Starting Hole' },
    { token: '{{division}}',    label: 'Division' },
    { token: '{{flight}}',      label: 'Flight' },
    { token: '{{groupNumber}}', label: 'Group #' },
    // ── event-level ───────────────────────────────────────────────────────────
    { token: '{{eventName}}',   label: 'Event Name' },
    { token: '{{courseName}}',  label: 'Course Name' },
    { token: '{{eventDate}}',   label: 'Event Date' },
    { token: '{{targetTime}}',  label: 'Target Time / Pace' },
    { token: '{{scoreFormat}}', label: 'Score Format' },
    { token: '{{playFormat}}',  label: 'Play Format' },
] as const;

export type ScorecardBindingToken = (typeof SCORECARD_BINDING_TOKENS)[number]['token'];

// ─── Binding resolver ─────────────────────────────────────────────────────────

/**
 * Replace all {{token}} placeholders in a text content string.
 *
 * @param content   - Raw content string potentially containing tokens.
 * @param reg       - Merged registration / scoring-unit record.
 * @param eventData - Event document (tbEvent) cast to a loose map.
 * @param roundValue - 1-based round index used to pick tee time.
 */
export interface ResolveScorecardBindingsOptions {
    /** When true, registration-level tokens resolve to empty strings. */
    blankRegistrationData?: boolean;
}

export function resolveScorecardBindings(
    content: string,
    reg: Record<string, unknown>,
    eventData: Record<string, unknown>,
    roundValue: number,
    options?: ResolveScorecardBindingsOptions,
): string {
    const blankReg = options?.blankRegistrationData ?? false;
    // ── Tee time from registration ────────────────────────────────────────────
    const teeTimeRaw = (reg.teeTimes as Array<{ teeTime?: string }> | undefined)?.[roundValue - 1]?.teeTime;
    const teeTimeFormatted = teeTimeRaw
        ? new Date(teeTimeRaw).toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              timeZone: 'UTC',
          })
        : '—';

    // ── Event date from startDate ─────────────────────────────────────────────
    const startDateRaw = eventData.startDate as string | Date | undefined;
    const eventDateFormatted = startDateRaw
        ? new Date(startDateRaw).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              timeZone: 'UTC',
          })
        : '—';

    // ── Pace / target time — prefer per-round, fall back to event-level ───────
    const rounds = eventData.rounds as Array<Record<string, unknown>> | undefined;
    const roundTarget = rounds?.[roundValue - 1]?.targetRoundLength as number | undefined;
    const eventTarget = eventData.roundLengthTarget as number | undefined;
    const targetMinutes = roundTarget ?? eventTarget;
    const targetTimeFormatted = targetMinutes != null ? `${targetMinutes} min` : '—';

    // ── Score / play format — prefer per-round, fall back to event-level ──────
    const roundScoreFormat = rounds?.[roundValue - 1]?.scoreFormat as string | undefined;
    const roundPlayFormat  = rounds?.[roundValue - 1]?.playFormat  as string | undefined;

    const startingHole = resolveRegistrationStartingHole(reg, roundValue, eventData);
    const regEmpty = blankReg ? '' : undefined;

    return content
        // registration-level
        .replace(/\{\{playerName\}\}/g,  regEmpty ?? String(reg.playerName  ?? ''))
        .replace(/\{\{teamName\}\}/g,    regEmpty ?? String(reg.teamName    ?? ''))
        .replace(/\{\{scoreCode\}\}/g,   regEmpty ?? String(reg.scoreCode   ?? ''))
        .replace(/\{\{teeTime\}\}/g,     regEmpty ?? teeTimeFormatted)
        .replace(/\{\{startingHole\}\}/g, regEmpty ?? startingHole)
        .replace(/\{\{division\}\}/g,    regEmpty ?? String(reg.division    ?? ''))
        .replace(/\{\{flight\}\}/g,      regEmpty ?? String(reg.flight      ?? ''))
        .replace(/\{\{groupNumber\}\}/g, regEmpty ?? String(reg.groupNumber ?? ''))
        // event-level
        .replace(/\{\{eventName\}\}/g,   String(eventData.eventName  ?? ''))
        .replace(/\{\{courseName\}\}/g,  String(eventData.courseName ?? ''))
        .replace(/\{\{eventDate\}\}/g,   eventDateFormatted)
        .replace(/\{\{targetTime\}\}/g,  targetTimeFormatted)
        .replace(/\{\{scoreFormat\}\}/g, String(roundScoreFormat ?? eventData.scoreFormat ?? ''))
        .replace(/\{\{playFormat\}\}/g,  String(roundPlayFormat  ?? eventData.playFormat  ?? ''));
}

// ─── Template helpers ─────────────────────────────────────────────────────────

export function templateUsesStartingHoleBinding(template: ScorecardTemplate): boolean {
    return template.elements.some(
        (el) => el.type === 'text' && (el.content ?? '').includes('{{startingHole}}'),
    );
}

export function visibleScorecardElements(elements: ScorecardElement[]): ScorecardElement[] {
    return elements.filter((el) => el.visible !== false);
}

/** First letter of each whitespace-delimited name part, uppercased. "John Doe" → "JD". */
export function playerNameToInitials(name: string): string {
    return name.trim().split(/\s+/).filter(Boolean)
        .map((part) => (part[0] ?? '').toUpperCase()).join('');
}

/** All registrations on a scoring unit → "JD / JS". */
export function scoringUnitInitialsLabel(unit: ScoringUnit): string {
    return unit.registrations
        .map((r) => playerNameToInitials(r.playerName ?? ''))
        .filter(Boolean)
        .join(' / ');
}

export function resolveHighlightHoleNumber(
    template: ScorecardTemplate,
    reg: Record<string, unknown>,
    eventData: Record<string, unknown>,
    roundValue: number,
): number | null {
    if (!templateUsesStartingHoleBinding(template)) return null;
    const raw = resolveRegistrationStartingHole(reg, roundValue, eventData);
    return parseStartingHoleNumber(raw);
}

// Re-export for convenience
export { parseStartingHoleNumber, resolveRegistrationStartingHole };

// ─── Tee-set resolver ─────────────────────────────────────────────────────────

/**
 * Return the CourseTeeSet matching the registration's teeSet name,
 * or the first available tee set when no match is found.
 */
export function resolveTeeSet(
    reg: Pick<TbRegistration, 'teeSet'>,
    courseData: CourseTeeSetData,
): CourseTeeSet | null {
    if (!courseData?.teeSets?.length) return null;
    if (reg.teeSet) {
        const match = courseData.teeSets.find((ts) => ts.teeName === reg.teeSet);
        if (match) return match;
    }
    return courseData.teeSets[0] ?? null;
}

// ─── Build scoring units from raw registrations ───────────────────────────────

/**
 * Merge a flat list of registrations into one ScoringUnit per score code.
 * Filters out canceled, withdrawn, disqualified, and refunded entries first.
 */
export function buildScoringUnits(
    registrations: TbRegistration[],
    roundValue: number,
): ScoringUnit[] {
    const active = registrations.filter(
        (r) => !r.canceled && !r.withdrawn && !r.disqualified && !r.refunded,
    );

    const map = new Map<string, TbRegistration[]>();
    for (const reg of active) {
        const code = reg.scoreCode ?? '';
        const group = map.get(code) ?? [];
        group.push(reg);
        map.set(code, group);
    }

    const units: ScoringUnit[] = [];
    for (const [scoreCode, regs] of map.entries()) {
        const first = regs[0];
        const teeTimeRaw = (first.teeTimes as Array<{ teeTime?: string }> | undefined)?.[roundValue - 1]?.teeTime;
        const teeTime = teeTimeRaw
            ? new Date(teeTimeRaw).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZone: 'UTC',
              })
            : null;

        units.push({
            scoreCode,
            eventScoreID: first.eventScoreID as string | undefined,
            playerName: regs.map((r) => r.playerName ?? '').join(' / '),
            playerNames: regs.map((r) => r.playerName ?? ''),
            initialsLabel: regs
                .map((r) => playerNameToInitials(r.playerName ?? ''))
                .filter(Boolean)
                .join(' / '),
            teeTime,
            teeSet: first.teeSet,
            registrationCount: regs.length,
            registrations: regs,
        });
    }

    // Sort by tee time (nulls last), then by combined player name
    return units.sort((a, b) => {
        if (a.teeTime && b.teeTime) return a.teeTime.localeCompare(b.teeTime);
        if (a.teeTime) return -1;
        if (b.teeTime) return 1;
        return a.playerName.localeCompare(b.playerName);
    });
}

// ─── Default template ─────────────────────────────────────────────────────────
// Canvas: 792 × 306 px (11" × 4.25")
//
// Zone layout (% of canvas height):
//   Marker strip   2.6–20%   Tear-away marker grid (par only, top position)
//   Header        27–44%    Event name, course name, date, tee time
//   Player grid   44–74%    Full-width scorecardGrid (par, hdcp, dist + auto player rows)
//   Signatures    82–92%    Player + Marker signature line/label pairs
//   QR / code     75–100%   Score code QR + text label

export const DEFAULT_SCORECARD_TEMPLATE: ScorecardTemplate = {
    backgroundColor: '#ffffff',
    elements: [
        // ── Marker tear-away grid (top) ───────────────────────────────────────
        {
            id: '2bbb3c07-eaf2-49a9-bc08-9fe14f704922',
            type: 'scorecardGrid',
            x: 1.3888888888888886,
            y: 2.6143790849673203,
            width: 95.58080808080808,
            height: 17.555555555555554,
            gridConfig: {
                showPar: true,
                showHandicap: false,
                showDistance: false,
                showPlayer: false,
                playerRows: 'auto',
                showMarker: true,
                showStrokes: false,
                showFoldGap: true,
                foldGapPx: 4,
                gridRole: 'marker',
                markerPosition: 'top',
                playerRowHeightPx: 20,
            },
        },
        // ── Header ───────────────────────────────────────────────────────────
        {
            id: 'default-event-name',
            type: 'text',
            x: 1.1262626262626263,
            y: 27.143790849673206,
            width: 45.4040404040404,
            height: 7,
            content: '{{eventName}}',
            fontSize: 14,
            fontWeight: 'bold',
            color: '#000000',
            textAlign: 'left',
        },
        {
            id: 'default-event-date',
            type: 'text',
            x: 80.36868686868686,
            y: 29.75816993464052,
            width: 16.737373737373733,
            height: 6.019607843137255,
            content: 'Date: {{eventDate}}',
            fontSize: 11,
            fontWeight: 'normal',
            color: '#000000',
            textAlign: 'right',
        },
        {
            id: 'default-course-name',
            type: 'text',
            x: 1.1262626262626263,
            y: 35.450980392156865,
            width: 45.025252525252526,
            height: 6.673202614379085,
            content: '{{courseName}}',
            fontSize: 12,
            fontWeight: 'normal',
            color: '#444444',
            textAlign: 'left',
        },
        {
            id: 'default-tee-time',
            type: 'text',
            x: 78.6010101010101,
            y: 36.43137254901961,
            width: 18.505050505050505,
            height: 6.019607843137255,
            content: 'Tee Time: {{teeTime}}',
            fontSize: 11,
            fontWeight: 'normal',
            color: '#444444',
            textAlign: 'right',
        },
        // ── Main scorecard grid ───────────────────────────────────────────────
        {
            id: 'default-scorecard-grid',
            type: 'scorecardGrid',
            x: 1.01010101010101,
            y: 44.1045751633987,
            width: 96.21212121212122,
            height: 29.64705882352942,
            gridConfig: {
                showPar: true,
                showHandicap: true,
                showDistance: true,
                showPlayer: true,
                playerRows: 'auto',
                showMarker: false,
                showStrokes: false,
                showFoldGap: true,
                foldGapPx: 4,
                gridRole: 'player',
                playerRowHeightPx: 25,
            },
        },
        // ── Signature block ───────────────────────────────────────────────────
        {
            id: 'default-player-sig-line',
            type: 'line',
            x: 14.131313131313131,
            y: 82.94117647058823,
            width: 30,
            height: 0.5,
            lineOrientation: 'horizontal',
            lineColor: '#000000',
            lineThickness: 1,
        },
        {
            id: 'default-player-sig-label',
            type: 'text',
            x: 14.015151515151516,
            y: 85.2483660130719,
            width: 29.848484848484848,
            height: 5.673202614379085,
            content: 'Player Signature',
            fontSize: 9,
            fontWeight: 'normal',
            color: '#666666',
            textAlign: 'left',
        },
        {
            id: 'default-marker-sig-line',
            type: 'line',
            x: 52,
            y: 82.94117647058823,
            width: 30,
            height: 0.5,
            lineOrientation: 'horizontal',
            lineColor: '#000000',
            lineThickness: 1,
        },
        {
            id: 'default-marker-sig-label',
            type: 'text',
            x: 51.87373737373738,
            y: 84.26797385620915,
            width: 30.090909090909093,
            height: 6.65359477124183,
            content: 'Marker Signature',
            fontSize: 9,
            fontWeight: 'normal',
            color: '#666666',
            textAlign: 'left',
        },
        // ── QR code + score code label ────────────────────────────────────────
        {
            id: '57caf579-5147-485d-9814-17d302657499',
            type: 'qrcode',
            x: 88.25757575757575,
            y: 75.88235294117648,
            width: 9.015151515151516,
            height: 21.24183006535948,
        },
        {
            id: '93f0ed13-c4a8-4924-8d1e-f8894a872212',
            type: 'text',
            x: 55.292929292929315,
            y: 91.84967320261437,
            width: 32.196969696969695,
            height: 5.712418300653594,
            content: "{{playerName}}'s Score Code: {{scoreCode}}",
            fontSize: 11,
            fontWeight: 'normal',
            color: '#000000',
            textAlign: 'right',
        },
    ],
};
